import React, { useState, useEffect, useRef } from "react";
import {
  AlertTriangle,
  RefreshCw,
  Shield,
  FileCode,
  AlertCircle,
  HelpCircle,
  ArrowUpCircle,
  ArrowDownCircle,
  FileText,
  Link as LinkIcon,
} from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import VulnerabilityItem from "../components/VulnerabilityItem";
import SecurityScore from "../components/SecurityScore";
import LogoutButton from "../components/LogoutButton";
import Pagination from "../components/Pagination";
import ReportGeneratorModal from "../components/ReportGeneratorModal";
import { useScan } from "../contexts/ScanContext";
import { getGuestToken, guestResultsPath, saveGuestScan, saveReturnPath, tokenFromHash } from "../utils/guestScans";

const ResultsPage = () => {
  const { scanId: paramScanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    vulnerabilities,
    repoUrl,
    resetScan,
    generateReport,
    loadScan,
    scanId: contextScanId,
    error,
    analyzing,
    user,
    authChecking,
    isGuest,
    claimGuestScans,
    pagination,
    counts,
    filters,
    changePage,
    changePageSize,
    applyFilter,
    clearFilters,
  } = useScan();
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const failedLoadRef = useRef(null);

  const hashToken = tokenFromHash(location.hash);

  useEffect(() => {
    // A guest scan's link carries its access token in the fragment (#t=...). Keep it, so the
    // API client can send it and the scan can be claimed after signing in. Saved here, before
    // the check below reads it, rather than during render.
    if (paramScanId && hashToken && getGuestToken(paramScanId) !== hashToken) {
      saveGuestScan({ scanId: paramScanId, token: hashToken });
    }
    if (authChecking) return;

    if (!user && !getGuestToken(paramScanId)) {
      setAccessError("Please log in to view scan results");
      return;
    }

    // Don't retry a load that already failed for this scan, user and token.
    const attemptKey = `${paramScanId}|${user ? "user" : "guest"}|${hashToken}`;
    if (failedLoadRef.current === attemptKey) return;
    setAccessError(null);

    if (paramScanId && paramScanId !== contextScanId) {
      const fail = (message) => {
        failedLoadRef.current = attemptKey;
        setAccessError(message);
      };
      (async () => {
        // A signed-in user opening a guest link adopts that scan into their account first.
        if (user && hashToken) await claimGuestScans();
        try {
          await loadScan(paramScanId);
        } catch (err) {
          const status = err.response?.status;
          if (!user && (status === 401 || status === 403 || status === 404)) {
            fail("This scan link is invalid or has expired.");
          } else if (status === 403) {
            fail("You are not authorized to view this scan");
          } else if (status === 401) {
            fail("Please log in to view scan results");
          } else if (status === 404) {
            fail("Scan not found, or you do not have access to it.");
          } else {
            fail("Error loading scan results");
          }
        }
      })();
    }
  }, [paramScanId, hashToken, contextScanId, loadScan, claimGuestScans, user, authChecking]);

  const handleCopyLink = async () => {
    const link = `${window.location.origin}${guestResultsPath(paramScanId, getGuestToken(paramScanId))}`;
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Could not copy the results link:", err);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const pageHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      const scrollThreshold = 100; // Pixels

      // Show/hide scroll to top button
      if (scrollY > scrollThreshold) {
        setShowScrollToTop(true);
      } else {
        setShowScrollToTop(false);
      }

      // Show/hide scroll to bottom button
      // Show if scrolled down a bit and not already near the bottom
      if (
        scrollY > scrollThreshold &&
        scrollY + viewportHeight < pageHeight - scrollThreshold
      ) {
        setShowScrollToBottom(true);
      } else {
        setShowScrollToBottom(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  const handleGenerateReport = () => {
    setShowReportModal(true);
  };

  // Sign in, then come back to this scan: login claims it, so it opens with every action.
  const signInAndReturn = () => {
    saveReturnPath(`/results/${encodeURIComponent(paramScanId)}`);
    navigate("/login");
  };

  const navigateToCompliance = () => {
    navigate("/compliance");
  };

  const handleNewScan = () => {
    resetScan();
    navigate("/scan");
  };

  if (accessError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">{accessError}</p>
          <button
            onClick={signInAndReturn}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (authChecking || (analyzing && !vulnerabilities.length)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-700">
        Loading results...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-700">
        Error loading results: {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div className="min-w-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
              Security Analysis Results
            </h2>
            <p className="text-gray-600">
              Repository: <span className="text-gray-900 break-all">{repoUrl}</span>
            </p>
          </div>
          {isGuest ? (
            <button
              onClick={handleCopyLink}
              className="self-start sm:self-auto flex items-center text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              <LinkIcon className="w-4 h-4 mr-1" />
              {linkCopied ? "Link copied" : "Copy link to these results"}
            </button>
          ) : (
            <LogoutButton />
          )}
        </div>

        {isGuest && (
          <div className="mb-6 bg-teal-50 border border-teal-200 rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-teal-900 text-sm">
              You are viewing this scan without an account. Sign in with GitHub to auto-fix findings,
              generate reports and keep this scan in your history.
            </p>
            <button
              onClick={signInAndReturn}
              className="w-full sm:w-auto flex-shrink-0 bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
            >
              Sign in
            </button>
          </div>
        )}

        <SecurityScore />

        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-4 sm:p-6 mb-8">
          <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2 flex-shrink-0 text-teal-600" />
              Detected Vulnerabilities
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {!isGuest && (
                <button
                  onClick={navigateToCompliance}
                  className="text-teal-600 hover:text-teal-700 flex items-center text-sm"
                >
                  <HelpCircle className="w-4 h-4 mr-1" />
                  Compliance Insights
                </button>
              )}
              <button
                onClick={handleNewScan}
                className="text-teal-600 hover:text-teal-700 flex items-center text-sm"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh Scan
              </button>
            </div>
          </div>

          {/* Filter Controls. Below `sm` the three label+select pairs each take a
              full row: as inline pairs the selects sized to their longest option
              ("Code Security (SAST)") and staggered off the right edge. */}
          <div className="mb-4 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-center sm:gap-4">
            {/* Category Filter */}
            <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:items-center sm:gap-2">
              <label className="text-sm text-gray-500">Category:</label>
              <select
                value={filters.category || ''}
                onChange={(e) => applyFilter('category', e.target.value || null)}
                className="w-full sm:w-auto px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All</option>
                <option value="code_security">Code Security (SAST)</option>
                <option value="third_party_dependencies">Dependencies (SCA)</option>
                <option value="secret_detection">Secret Detection</option>
              </select>
            </div>

            {/* Severity Filter */}
            <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:items-center sm:gap-2">
              <label className="text-sm text-gray-500">Severity:</label>
              <select
                value={filters.severity || ''}
                onChange={(e) => applyFilter('severity', e.target.value || null)}
                className="w-full sm:w-auto px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:items-center sm:gap-2">
              <label className="text-sm text-gray-500">Status:</label>
              <select
                value={filters.status || ''}
                onChange={(e) => applyFilter('status', e.target.value || null)}
                className="w-full sm:w-auto px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="fixed">Fixed</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            {(filters.category || filters.severity || filters.status) && (
              <button
                onClick={clearFilters}
                className="w-full sm:w-auto px-4 py-2 min-h-[44px] sm:min-h-0 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            )}

            {/* Count Badges */}
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                Total: {counts.total}
              </span>
              <span className="px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-sm">
                Critical: {counts.by_severity?.critical || 0}
              </span>
              <span className="px-3 py-1 bg-orange-50 text-orange-700 border border-orange-200 rounded text-sm">
                High: {counts.by_severity?.high || 0}
              </span>
            </div>
          </div>

          {/* Pagination - Top */}
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            pageSize={pagination.limit}
            onPageChange={changePage}
            onPageSizeChange={changePageSize}
            isLoading={analyzing}
            className="border-t border-gray-100 rounded-t-lg"
          />

          {/* Vulnerabilities List - Flat rendering */}
          <div className="space-y-2 my-4">
            {analyzing && vulnerabilities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading vulnerabilities...</div>
            ) : vulnerabilities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {(filters.category || filters.severity || filters.status)
                  ? 'No vulnerabilities match the selected filters.'
                  : 'No vulnerabilities found.'}
              </div>
            ) : (
              vulnerabilities.map(vuln => (
                <VulnerabilityItem key={vuln.id} vulnerability={vuln} readOnly={isGuest} />
              ))
            )}
          </div>

          {/* Pagination - Bottom */}
          {pagination.totalPages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              pageSize={pagination.limit}
              onPageChange={changePage}
              onPageSizeChange={changePageSize}
              isLoading={analyzing}
              className="border-t border-gray-100 rounded-b-lg"
            />
          )}
        </div>

        {/* `flex-col-reverse` below `sm` keeps the two primary actions above the
            secondary "Scan Another Repository". `pb-20` clears the scroll FABs,
            which are fixed over the bottom-right corner. */}
        <div className="flex flex-col-reverse gap-3 pb-20 sm:flex-row sm:justify-between sm:pb-0">
          <button
            onClick={handleNewScan}
            className="w-full sm:w-auto bg-white border border-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Scan Another Repository
          </button>

          {!isGuest && (
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <button
              onClick={navigateToCompliance}
              className="w-full sm:w-auto bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center"
            >
              <HelpCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              Compliance Insights
            </button>
            <button
              onClick={handleGenerateReport}
              className="w-full sm:w-auto bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center"
            >
              <FileText className="w-5 h-5 mr-2 flex-shrink-0" />
              Generate Security Report
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Report Generator Modal */}
      {showReportModal && !isGuest && (
        <ReportGeneratorModal
          scanId={contextScanId || paramScanId}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {/* Scroll Buttons Container */}
      <div className="fixed right-4 bottom-4 sm:right-8 sm:bottom-8 flex flex-col space-y-3 z-50">
        {showScrollToTop && (
          <button
            onClick={handleScrollToTop}
            className="p-3 bg-teal-600 text-white rounded-full shadow-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
            aria-label="Scroll to top"
          >
            <ArrowUpCircle className="w-6 h-6" />
          </button>
        )}
        {showScrollToBottom && (
          <button
            onClick={handleScrollToBottom}
            className="p-3 bg-teal-600 text-white rounded-full shadow-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
            aria-label="Scroll to bottom"
          >
            <ArrowDownCircle className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ResultsPage;
